/**
 * Breadcrumbs components.
 * @module components/theme/Breadcrumbs/Breadcrumbs
 */

import { useEffect, useState } from 'react';
import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import cx from 'classnames';
import messages from 'govbr.frontend.policy/messages/breadcrumbs';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { compose } from 'redux';

import styles from './Breadcrumbs.module.less';

const Breadcrumbs = ({ root, items, getBreadcrumbs, pathname, intl }) => {
  const [mobilActive, setMobilActive] = useState(false);

  useEffect(() => {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(pathname))) {
      getBreadcrumbs(getBaseUrl(pathname));
    }
  }, [pathname, getBreadcrumbs]);

  // Vazio
  if (!items?.length || !pathname) {
    return null;
  }

  const showHomeOnMobile = items.length <= 1;
  const showFolder = items.length > 1;

  const arrow = <i className={`icon fas fa-chevron-right ${styles.icon}`} />;

  const homeClass = cx('crumb home', {
    'd-none d-sm-flex': !showHomeOnMobile,
    'd-flex': showHomeOnMobile,
  });
  const homeItem = (
    <li className={homeClass}>
      <Link
        to={root || '/'}
        className="br-button circle d-flex"
        title={intl.formatMessage(messages.home)}
      >
        <i className="fas fa-home"></i>
      </Link>
    </li>
  );

  // Dropdown mobile trigger
  const mobilTrigger = showFolder ? (
    <li className="crumb menu-mobil d-md-none">
      {arrow}
      <button
        className="br-button circle"
        onClick={() => {
          setMobilActive(!mobilActive);
        }}
      >
        <span className="sr-only">Botão Menu</span>
        <i className={`fas fa-folder-${mobilActive ? 'minus' : 'plus'}`} />
      </button>
    </li>
  ) : null;

  const path = [...items];

  // Nível atual
  const current = path.pop();
  const currentItem = (
    <li className="crumb" data-active="active">
      {arrow}
      <span>{current.title}</span>
    </li>
  );

  // Dropdown mobile
  const mobilItems = (
    <div className="br-card d-md-none">
      {path.map((item) => (
        <div className="br-item" key={item.url}>
          <Link to={item.url}>{item.title}</Link>
        </div>
      ))}
      <li className="br-item d-sm-none">
        <Link to={root || '/'}>{intl.formatMessage(messages.home)}</Link>
      </li>
    </div>
  );

  // Níveis anteriores
  const prevItems =
    !!path.length &&
    path.map((item) => (
      <li className="crumb d-none d-md-flex" key={item.url}>
        {arrow}
        <Link to={item.url}>{item.title}</Link>
      </li>
    ));

  return (
    <div className="ui container">
      <nav className="br-breadcrumb my-1" aria-label="Trilha de navegação">
        <ol className="crumb-list">
          {homeItem}
          {mobilTrigger}
          {prevItems}
          {currentItem}
        </ol>
        {!!path.length && mobilActive && mobilItems}
      </nav>
      <span className="br-divider"></span>
    </div>
  );
};

Breadcrumbs.propTypes = {
  getBreadcrumbs: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  root: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  ).isRequired,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      items: state.breadcrumbs.items,
      root: state.breadcrumbs.root,
    }),
    { getBreadcrumbs },
  ),
)(Breadcrumbs);
